import { Container, Grid } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import { treeViewUtils } from '@/utils';
import itemUtils from '@/utils/itemUtils';

import type { Item, Pagination } from '../../models';
import { enhanceFetchedAssets } from '../../pages/assets/asset-listing/enhanceFetchedAsset';
import { FuelEntityType, FuelPortalService } from '../../services';
import { AllAssetFilterTypeOptions, type AssetFilterTypeOptions } from '../../store/forms/Asset-Editor/AssetFilterTypeOptions';
import { applicationDataSelector } from '../../store/slices/application-data';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import clientUtils from '../../utils/clientUtils';
import { dateUtils } from '../../utils/dateUtils';
import FuelAutoComplete from '../fuel-autocomplete';
import FuelSearchInput from '../fuel-search-input';
import FuelTable from '../fuel-table/FuelTable';
import FuelTags from '../fuel-tags';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';
import type { DropdownOption } from '../VNDropdown';
import { VNDropdown, VNDropdownOUOptions } from '../VNDropdown';

const initialPagination: Pagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

interface Iprops {
  showEmbargoColumns?: boolean;
  isBroadcastChannel?: boolean;
  channelVariants?: string[];
  assetFilterTypes?: AssetFilterTypeOptions[];
  excludeAssetFilterTypes?: AssetFilterTypeOptions[];
  showStatus?: boolean;
  allowSeriesAndSeasons?: boolean;
}

const FuelAssetSearch = ({
  showEmbargoColumns = false,
  isBroadcastChannel = false,
  channelVariants = [],
  assetFilterTypes = [],
  excludeAssetFilterTypes = [],
  showStatus = false,
  allowSeriesAndSeasons = false,
}: Iprops) => {
  const [pagination, setPagination] = useState<Pagination>(null);
  const { ouData, schemas, categories } = useSelector(applicationDataSelector);
  const [selectedAssetFilterTypes, setSelectedAssetFilterTypes] = useState<AssetFilterTypeOptions[]>(assetFilterTypes);
  const [assetFilterTypeOptions, setAssetFilterTypeOptions] = useState<AssetFilterTypeOptions[]>();
  // const [selectedCategories, setSelectedCategories] = useState<any>([]);
  // const [selectedTags, setSelectedTags] = useState<any>([]);
  const [items, setItems] = useState<any>([]);
  // const [searchKeyword, setSearchKeyword] = useState('');
  const { permissions, stage, userAccessLoadingLoading, accessibleOUs, fuelUserAccessData } =
    useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const { loading, setProgress } = useProgress();
  const [ouOptions, setOuOptions] = useState<DropdownOption[]>([]);
  const ouForm = useForm<{ ouSelect: (string | number)[] }>({ defaultValues: { ouSelect: [] } });

  const accessibleOUList = useMemo(() => {
    const currentOuId = ouData.ouId;

    const childOUs = accessibleOUs.filter((ou) => ou.parentOuId === currentOuId);
    if (childOUs.length === 0) {
      setOuOptions([]);
      return [];
    }

    const filteredOUs = accessibleOUs.filter((ou) => ou.ouId === currentOuId || ou.parentOuId === currentOuId);
    const flatOptions: DropdownOption[] = treeViewUtils.createDataTreeArray(filteredOUs).map((item) => ({
      id: item.ou.ouId,
      label: item.ou.ouName,
      depth: item.depth,
      childCount: item.childCount,
    }));
    setOuOptions(flatOptions);
    return flatOptions;
  }, [accessibleOUs]);

  const stateRef = useRef({ searchText: '', selectedCategories: [], selectedTags: [], selectedOUIds: [] });
  const ouInitializedRef = useRef(false);

  useEffect(() => {
    doSearch();
  }, []); // <-- empty dependency array

  const watchedOuSelect = ouForm.watch('ouSelect');
  useEffect(() => {
    if (!ouInitializedRef.current) {
      ouInitializedRef.current = true;
      return;
    }
    stateRef.current.selectedOUIds = (watchedOuSelect ?? []) as string[];
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedOuSelect]);

  const getPagination = () => {
    // reset the pagination to the first page
    let paginationNew = pagination;
    if (!paginationNew) {
      paginationNew = _.cloneDeep(initialPagination);
      paginationNew.page = 1;
    }
    return paginationNew;
  };

  const columnHelper = createColumnHelper<any>();

  const Thumbnail = useCallback(
    (cell) => <FuelAssetThumbnail item={cell.row.original} permissions={permissions} hideStatus={!showStatus} />,
    [permissions, showStatus]
  );

  const getColumns = (columnHelper, showEmbargoColumns = false, isBroadcastChannel = false) => {
    const embargoColumns = [
      columnHelper.accessor('handle', {
        header: () => '',
        width: '25px',
        sortable: false,
        cell: () => (
          <i aria-hidden="true" className="icon material-icons">
            reorder
          </i>
        ),
      }),
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Name',
        width: 170,
        sortable: true,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('embargoDate', {
        header: () => `Embargo (${ouData.timezoneAbbreviation})`,
        width: 130,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('expireDate', {
        header: `Expiry (${ouData.timezoneAbbreviation})`,
        width: 130,
        sortable: true,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        width: 75,
        sortable: true,
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    const defaultColumns = [
      columnHelper.accessor('handle', {
        header: () => '',
        width: '25px',
        sortable: false,
        cell: () => (
          <i aria-hidden="true" className="icon material-icons">
            reorder
          </i>
        ),
      }),
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        width: 170,
        sortable: true,
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} />,
      }),
      columnHelper.accessor('publicationDate', {
        header: `Publication (${ouData.timezoneAbbreviation})`,
        width: 150,
        sortable: false,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        footer: (props) => props.column.id,
        width: 130,
        sortable: true,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        footer: (props) => props.column.id,
        sortable: true,
        width: 130,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        width: 75,
        sortable: true,
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    const broadcastChannelColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        width: 170,
        sortable: true,
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} />,
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        width: 75,
        sortable: true,
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    if (isBroadcastChannel) {
      return broadcastChannelColumns;
    }

    if (showEmbargoColumns) {
      return embargoColumns;
    }
    return defaultColumns;
  };

  const columns = getColumns(columnHelper, showEmbargoColumns, isBroadcastChannel);

  const doSearch = async () => {
    // console.log('doSearch', { excludeAssetFilterTypes });
    setProgress(true);
    const myPagination = getPagination();
    const { searchText, selectedCategories, selectedTags } = stateRef.current;
    const categoryNames = selectedCategories.map((c) => {
      return c.name;
    });
    const resolveSortBy = () => {
      switch (myPagination.sortBy) {
        case 'categoryString':
          return 'categories';
        case 'tagString':
          return 'tags';
        case 'durationString':
          return 'duration';
        default:
          return myPagination.sortBy;
      }
    };

    // #2916 always exclude series and season by default
    // if (!allowSeriesAndSeasons) {
    //   excludeAssetFilterTypes.push({ id: AssetFilterType.Collection, name: AssetFilterType.Collection });
    // }
    const searchExcludeAssetFilterTypes = _.uniqBy(excludeAssetFilterTypes, 'id');
    const ouIds = stateRef.current.selectedOUIds.length > 0 ? stateRef.current.selectedOUIds : [ouData.ouId];
    const results = await FuelPortalService.searchAsset(
      searchText,
      ouIds,
      myPagination.page - 1,
      myPagination.rowsPerPage,
      categoryNames,
      selectedTags,
      resolveSortBy(),
      myPagination.descending ? 'desc' : 'asc',
      true,
      'clip',
      'Video',
      false,
      selectedAssetFilterTypes,
      searchExcludeAssetFilterTypes
    );
    const paginationObj = myPagination;
    if (results && results.data) {
      const thumbnailIds = itemUtils.getExternalThumbnailIds(results.data);
      const thumbnails: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, thumbnailIds);
      const assets = enhanceFetchedAssets(
        results.data,
        permissions,
        flags,
        true,
        channelVariants,
        [],
        ouData,
        null,
        null,
        thumbnails
      );

      paginationObj.totalItems = results.pagination.total;
      paginationObj.pages = Math.ceil(paginationObj.totalItems / paginationObj.rowsPerPage);
      setPagination(paginationObj);
      setItems(assets);
    } else {
      paginationObj.totalItems = 0;
      paginationObj.pages = 0;
      setPagination(paginationObj);
      setItems([]);
    }
    setProgress(false);
  };

  const paginationChange = (value: Pagination) => {
    // console.log('paginationChange', value);
    setPagination(value);
    throttledCallback();
  };

  const onCategoriesChanged = (value) => {
    stateRef.current.selectedCategories = value;
    // setSelectedCategories(value);
    throttledCallback();
  };

  const onTagsChanged = (value) => {
    // console.log('onTagsChanged');
    stateRef.current.selectedTags = value;
    // setSelectedTags(value);
    throttledCallback();
  };

  const onSearchClick = (value) => {
    // console.log('onSearchClick');
    stateRef.current.searchText = value;
    const paginationObject = pagination;
    paginationObject.page = 1;
    paginationObject.totalItems = 0;
    paginationObject.pages = 1;
    throttledCallback();
  };

  const throttledCallback = _.debounce(function () {
    doSearch();
  }, 0);

  const onSearchTextChanged = (value) => {
    // console.log('onSearchTextChanged', { value });
    stateRef.current.searchText = value;
    const paginationObject = pagination;
    paginationObject.page = 1;
    paginationObject.totalItems = 0;
    paginationObject.pages = 1;
    throttledCallback();
  };

  const onAssetFilterTypesChanged = (val) => {
    // console.log('onAssetFilterTypesChanged', { val });
    setSelectedAssetFilterTypes(val);
  };

  const getAssetFilterTypeOptions = () => {
    let items = AllAssetFilterTypeOptions;
    if (excludeAssetFilterTypes.length > 0) {
      items = _.differenceWith(AllAssetFilterTypeOptions, excludeAssetFilterTypes, _.isEqual);
    }
    return items;
  };

  return (
    <div className="fullHeight fuel-asset-search">
      <Container className="fullHeight" component="section" maxWidth="lg">
        <Grid className="asset-search-controls" container spacing={1}>
          <Grid item xs={12}>
            <FuelSearchInput
              onSearch={onSearchClick}
              onChange={onSearchTextChanged}
              treeComponent={
                accessibleOUList.length > 0 ? (
                  <VNDropdown
                    compact
                    multiple
                    name="ouSelect"
                    placeholder="Portal(s)"
                    searchable="inset"
                    options={ouOptions}
                    control={ouForm.control}
                    listComponent={VNDropdownOUOptions}
                  />
                ) : null
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FuelTags
              id="fuel-ingest-tag-id"
              tags={stateRef.current.selectedTags}
              inputTagWidth={365}
              onChange={onTagsChanged}
              ouIds={(watchedOuSelect?.length > 0 ? watchedOuSelect : [ouData?.ouId]) as string[]}
            />
          </Grid>
          <Grid item xs={6}>
            <FuelAutoComplete
              id="category-select"
              items={categories}
              itemText="name"
              itemKey="id"
              labelText="Categories"
              placeHolderText="Select Categories"
              controlWidth={365}
              multiSelect
              allowEmptyValue
              labelPaddingTop="-5px"
              defaultValue={stateRef.current.selectedCategories}
              onChange={onCategoriesChanged}
            />
          </Grid>

          <Grid item xs={6}>
            <FuelAutoComplete
              id="asset-type-select"
              items={getAssetFilterTypeOptions()}
              itemText="name"
              itemKey="id"
              labelText="Asset Types"
              placeHolderText="Select Asset Type"
              multiSelect
              defaultValue={selectedAssetFilterTypes}
              onChange={onAssetFilterTypesChanged}
            />
          </Grid>
        </Grid>
        <div className="asset-search-table-wrapper">
          <FuelTable
            columnHelper={columnHelper}
            data={items}
            defaultColumns={columns}
            paginationDetail={pagination}
            paginationChange={paginationChange}
            showRowSelection={false}
            loading={loading}
            tableHeight={42}
            pull
            id="fuel-asset-search-table"
          />
        </div>
      </Container>
    </div>
  );
};

export default FuelAssetSearch;
